import { Controller } from '@hotwired/stimulus'
import { createApp } from 'vue'
import MicronutrientsPanel from '~/javascript/components/diet/components/micronutrients_panel.vue'
import { FDA_DAILY_VALUES } from '~/javascript/components/diet/fda_daily_values.js'

/**
 * Stimulus controller for mounting the MicronutrientsPanel Vue component
 *
 * Usage in ERB:
 *   <div data-controller="diet--micronutrients"
 *        data-diet--micronutrients-fat-breakdown-value="<%= @summary.fat_breakdown.to_json %>"
 *        data-diet--micronutrients-micronutrients-value="<%= @summary.micronutrients.to_json %>"
 *        data-diet--micronutrients-targets-value="<%= @goal&.extended_targets&.to_json || '{}' %>">
 *   </div>
 */
export default class extends Controller {
  static values = {
    fatBreakdown: { type: Object, default: {} },
    micronutrients: { type: Object, default: {} },
    targets: { type: Object, default: {} }
  }

  connect() {
    this.mountVueComponent()
  }

  disconnect() {
    if (this.vueApp) {
      this.vueApp.unmount()
      this.vueApp = null
    }
  }

  /**
   * Convert snake_case keys to camelCase for Vue component
   * Ruby extended_targets uses snake_case (saturated_fat, vitamin_b6)
   * Vue/FDA_DAILY_VALUES uses camelCase (saturatedFat, vitaminB6)
   */
  snakeToCamel(obj) {
    const result = {}
    for (const [key, value] of Object.entries(obj)) {
      const camelKey = key.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase())
      result[camelKey] = value
    }
    return result
  }

  mountVueComponent() {
    // Convert snake_case targets from Ruby to camelCase for Vue
    const userTargets = this.snakeToCamel(this.targetsValue)

    // Merge user targets with FDA defaults for fallback display
    const effectiveTargets = {
      ...FDA_DAILY_VALUES,
      ...userTargets
    }

    this.vueApp = createApp(MicronutrientsPanel, {
      fatBreakdown: this.fatBreakdownValue,
      micronutrients: this.micronutrientsValue,
      targets: effectiveTargets
    })

    this.vueApp.mount(this.element)
  }

  // Value changed callbacks for dynamic updates
  fatBreakdownValueChanged() {
    if (this.vueApp) {
      this.disconnect()
      this.mountVueComponent()
    }
  }

  micronutrientsValueChanged() {
    if (this.vueApp) {
      this.disconnect()
      this.mountVueComponent()
    }
  }

  targetsValueChanged() {
    if (this.vueApp) {
      this.disconnect()
      this.mountVueComponent()
    }
  }
}
